/**
 * Parámetros de correo del despacho — remitente, SMTP, asunto/texto, pie y logo/firma.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  CheckCircle2,
  ChevronDown,
  ExternalLink,
  Loader2,
  MailCheck,
  ShieldCheck,
  Trash2,
  Upload,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { DespachoSectionCard } from "@/components/despacho/DespachoUi";
import SpellcheckTextarea from "@/components/SpellcheckTextarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  EMAIL_TRANSPORT_OAUTH_GOOGLE,
  EMAIL_TRANSPORT_OAUTH_MICROSOFT,
  EMAIL_TRANSPORT_OPTIONS,
  EMAIL_TRANSPORT_PLATFORM,
  EMAIL_TRANSPORT_SMTP,
  SMTP_PROVIDER_CUSTOM,
  SMTP_PROVIDERS,
  applySmtpProviderToForm,
  buildEmailConfigSavePayload,
  emailTransportStatusLabel,
  inferSmtpHost,
  inferSmtpProvider,
  isMicrosoftEmail,
  isOAuthTransport,
  platformSmtpReady,
  resolveEmailTransport,
  smtpPendingHint,
  smtpPresetForProvider,
  syncSmtpProviderFromEmail,
  usesPlatformEmail,
} from "@/lib/smtpPresets";
import {
  despachoMensajesGeneralesApi,
  parseMensajesGeneralesError,
} from "@/modules/despacho/despachoMensajesGeneralesApi";
import { useAuth } from "@/lib/auth";
import { useDesignerUiAccess } from "@/lib/designerUiAccess";
import { fetchPlatformEmailOauth, savePlatformEmailOauth } from "@/lib/platformEmailOauth";
import {
  GoogleBuyerSetupGuide,
  MicrosoftBuyerSetupGuide,
  OperatorOauthSetupGuide,
  PlatformEmailSetupGuide,
  SmtpBuyerSetupGuide,
} from "@/pages/despacho/emailSetupGuides";

const EMPTY_FORM = {
  enabled: false,
  use_platform_smtp: true,
  email_transport: EMAIL_TRANSPORT_PLATFORM,
  from_email: "",
  from_name: "",
  reply_to: "",
  subject_prefix: "",
  default_subject: "",
  default_body: "",
  footer_text: "",
  append_footer: true,
  smtp_host: "",
  smtp_port: 587,
  smtp_user: "",
  smtp_provider: SMTP_PROVIDER_CUSTOM,
  smtp_password: "",
  smtp_use_tls: true,
  clear_smtp_password: false,
};

export default function CalendarioEmailParamsPanel({ readOnly = false, onOpenEnvioLibre }) {
  const { user } = useAuth();
  const { seesDesignerModule } = useDesignerUiAccess(user);
  const [loadState, setLoadState] = useState("loading");
  const [saving, setSaving] = useState(false);
  const [cfgMeta, setCfgMeta] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [previewToken, setPreviewToken] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [connectingOAuth, setConnectingOAuth] = useState("");
  const [platformOauth, setPlatformOauth] = useState(null);
  const [platformOauthForm, setPlatformOauthForm] = useState({
    google_client_id: "",
    google_client_secret: "",
    microsoft_client_id: "",
    microsoft_client_secret: "",
    microsoft_tenant: "common",
  });
  const [savingPlatformOauth, setSavingPlatformOauth] = useState(false);
  const [platformOauthOpen, setPlatformOauthOpen] = useState(false);
  const [advancedTransportOpen, setAdvancedTransportOpen] = useState(false);
  const platformOauthRef = useRef(null);

  const canEditPlatformOauth = Boolean(
    cfgMeta?.platform_oauth_can_edit
    || user?.is_platform_designer
    || user?.can_generate_licenses
    || seesDesignerModule,
  );

  const loadPlatformOauth = useCallback(async () => {
    if (!canEditPlatformOauth) {
      setPlatformOauth(null);
      return;
    }
    try {
      const data = await fetchPlatformEmailOauth();
      setPlatformOauth(data);
      setPlatformOauthForm((prev) => ({
        ...prev,
        google_client_id: data.google_client_id || "",
        microsoft_client_id: data.microsoft_client_id || "",
        microsoft_tenant: data.microsoft_tenant || "common",
        google_client_secret: "",
        microsoft_client_secret: "",
      }));
    } catch (err) {
      setPlatformOauth(null);
      const detail = err?.response?.data?.detail;
      if (detail) {
        toast.error(typeof detail === "string" ? detail : "No se pudo cargar la configuración OAuth de plataforma");
      }
    }
  }, [canEditPlatformOauth]);

  const load = useCallback(async () => {
    setLoadState("loading");
    try {
      const cfg = await despachoMensajesGeneralesApi.getEmailConfig();
      setCfgMeta(cfg);
      const provider = cfg.smtp_provider || inferSmtpProvider(cfg.from_email);
      const platformEmail = usesPlatformEmail(cfg);
      const transport = resolveEmailTransport(cfg);
      setForm({
        enabled: cfg.id ? Boolean(cfg.enabled) : true,
        use_platform_smtp: platformEmail,
        email_transport: transport,
        from_email: cfg.from_email || "",
        from_name: cfg.from_name || "",
        reply_to: cfg.reply_to || "",
        subject_prefix: cfg.subject_prefix || "",
        default_subject: cfg.default_subject || "",
        default_body: cfg.default_body || "",
        footer_text: cfg.footer_text || "",
        append_footer: cfg.append_footer !== false,
        smtp_provider: provider,
        smtp_host: cfg.smtp_host || inferSmtpHost(cfg.from_email) || "",
        smtp_port: cfg.smtp_port || 587,
        smtp_user: cfg.smtp_user || cfg.from_email || "",
        smtp_password: "",
        smtp_use_tls: cfg.smtp_use_tls !== false,
        clear_smtp_password: false,
      });
      setAdvancedTransportOpen(
        transport !== EMAIL_TRANSPORT_PLATFORM
        || !platformSmtpReady(cfg),
      );
      setLoadState("ready");
    } catch (err) {
      setLoadState("error");
      toast.error(parseMensajesGeneralesError(err).message);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!canEditPlatformOauth || loadState !== "ready") return;
    const transport = form.email_transport || resolveEmailTransport(cfgMeta || {});
    const providers = cfgMeta?.oauth_providers || {};
    const needsGoogle = transport === EMAIL_TRANSPORT_OAUTH_GOOGLE && !providers.google?.configured;
    const needsMicrosoft = transport === EMAIL_TRANSPORT_OAUTH_MICROSOFT && !providers.microsoft?.configured;
    if (needsGoogle || needsMicrosoft) {
      setPlatformOauthOpen(true);
    }
  }, [canEditPlatformOauth, loadState, form.email_transport, cfgMeta]);

  useEffect(() => {
    loadPlatformOauth();
  }, [loadPlatformOauth]);

  useEffect(() => {
    const onOAuthDone = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== "vf_email_oauth_done") return;
      if (event.data.status === "success") {
        toast.success("Cuenta de correo conectada correctamente.");
      } else if (event.data.detail) {
        toast.error(event.data.detail);
      } else {
        toast.error("No se pudo conectar la cuenta de correo.");
      }
      load();
    };
    window.addEventListener("message", onOAuthDone);
    return () => window.removeEventListener("message", onOAuthDone);
  }, [load]);

  const buildSavePayload = useCallback((formState) => buildEmailConfigSavePayload(formState), []);

  const handleSave = async () => {
    if (readOnly) return;
    const transport = form.email_transport || EMAIL_TRANSPORT_PLATFORM;
    const contactEmail = form.from_email.trim();
    if (
      [EMAIL_TRANSPORT_PLATFORM, EMAIL_TRANSPORT_SMTP].includes(transport)
      && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail)
    ) {
      toast.error("Indique un correo de contacto válido.");
      return;
    }
    if (
      (transport === EMAIL_TRANSPORT_OAUTH_GOOGLE || transport === EMAIL_TRANSPORT_OAUTH_MICROSOFT)
      && contactEmail
      && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail)
    ) {
      toast.error("Indique el correo de la cuenta que conectará.");
      return;
    }
    if (transport === EMAIL_TRANSPORT_SMTP && isMicrosoftEmail(contactEmail)) {
      toast.error("Outlook, Hotmail y Live requieren «Conectar Microsoft».");
      return;
    }
    if (
      transport === EMAIL_TRANSPORT_SMTP
      && !form.smtp_password.trim()
      && !cfgMeta?.has_smtp_password
    ) {
      toast.error("Indique la contraseña de aplicación de su proveedor.");
      return;
    }
    const oauthConnected =
      (transport === EMAIL_TRANSPORT_OAUTH_GOOGLE && cfgMeta?.oauth_connected && cfgMeta?.oauth_provider === "google")
      || (transport === EMAIL_TRANSPORT_OAUTH_MICROSOFT && cfgMeta?.oauth_connected && cfgMeta?.oauth_provider === "microsoft");
    setSaving(true);
    try {
      const cfg = await despachoMensajesGeneralesApi.putEmailConfig(buildSavePayload(form));
      setCfgMeta(cfg);
      setForm((f) => ({ ...f, smtp_password: "", clear_smtp_password: false }));
      if (isOAuthTransport(transport) && !oauthConnected) {
        toast.success("Guardado. Conecte su cuenta.");
      } else {
        toast.success("Guardado.");
      }
    } catch (err) {
      toast.error(parseMensajesGeneralesError(err).message);
    } finally {
      setSaving(false);
    }
  };

  const handleTestSmtp = async () => {
    if (readOnly) return;
    setTestingSmtp(true);
    try {
      const pwd = form.smtp_password.trim();
      const data = await despachoMensajesGeneralesApi.testSmtp({
        smtpPassword: pwd || undefined,
        usePlatformSmtp: form.use_platform_smtp,
      });
      const result = data.result || {};
      if (data.ok) {
        toast.success(`SMTP correcto (${data.smtp_user || ""} @ ${data.smtp_host || ""})`);
      } else {
        const detail = result.detail || "No se pudo autenticar con Gmail/SMTP.";
        toast.error(detail);
        if (
          !form.use_platform_smtp
          && form.smtp_provider === "gmail"
          && /535|contraseña de aplicación|gmail rechazó/i.test(detail)
        ) {
          toast.message("Sugerencia", {
            description: "Active «Usar servidor de correo de la plataforma» (arriba) y guarde, sin contraseña Gmail.",
          });
        }
      }
    } catch (err) {
      toast.error(parseMensajesGeneralesError(err).message);
    } finally {
      setTestingSmtp(false);
    }
  };

  const handleTransportChange = (transportId) => {
    setAdvancedTransportOpen(transportId !== EMAIL_TRANSPORT_PLATFORM);
    setForm((f) => {
      const next = {
        ...f,
        email_transport: transportId,
        use_platform_smtp: transportId === EMAIL_TRANSPORT_PLATFORM,
        clear_smtp_password: isOAuthTransport(transportId) ? true : f.clear_smtp_password,
      };
      if (transportId === EMAIL_TRANSPORT_SMTP && f.from_email) {
        return applySmtpProviderToForm(next, inferSmtpProvider(f.from_email), { fromEmail: f.from_email });
      }
      if (isOAuthTransport(transportId)) {
        return {
          ...next,
          smtp_host: "",
          smtp_provider: SMTP_PROVIDER_CUSTOM,
        };
      }
      return next;
    });
  };

  const openPlatformOauthPanel = useCallback(() => {
    setPlatformOauthOpen(true);
    requestAnimationFrame(() => {
      platformOauthRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
  }, []);

  const renderOAuthConnectActions = (provider) => {
    const providers = cfgMeta?.oauth_providers || {};
    const isGoogle = provider === "google";
    const configured = Boolean(providers[provider]?.configured);
    const connecting = connectingOAuth === provider;
    const label = isGoogle ? "Conectar Google" : "Conectar Microsoft";
    const testId = isGoogle ? "email-params-connect-google" : "email-params-connect-microsoft";

    if (!configured && canEditPlatformOauth) {
      return (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="border-amber-400 bg-white text-amber-950 hover:bg-amber-100"
          onClick={openPlatformOauthPanel}
          data-testid={`email-params-setup-${provider}-oauth`}
        >
          Configurar OAuth (operador)
        </Button>
      );
    }

    return (
      <>
        <Button
          type="button"
          size="sm"
          className="bg-[#0F2D5C] hover:bg-[#0a2248]"
          disabled={!configured || connecting}
          onClick={() => handleConnectOAuth(provider)}
          data-testid={testId}
        >
          {connecting ? <Loader2 className="h-4 w-4 animate-spin" /> : label}
        </Button>
        {!configured ? (
          <p className="m-0 text-amber-800 text-[11px]">Aún no disponible. Use la plataforma.</p>
        ) : null}
      </>
    );
  };

  const handleSavePlatformOauth = async () => {
    setSavingPlatformOauth(true);
    try {
      const body = {
        google_client_id: platformOauthForm.google_client_id.trim(),
        microsoft_client_id: platformOauthForm.microsoft_client_id.trim(),
        microsoft_tenant: platformOauthForm.microsoft_tenant.trim() || "common",
      };
      if (platformOauthForm.google_client_secret.trim()) {
        body.google_client_secret = platformOauthForm.google_client_secret.trim();
      }
      if (platformOauthForm.microsoft_client_secret.trim()) {
        body.microsoft_client_secret = platformOauthForm.microsoft_client_secret.trim();
      }
      const data = await savePlatformEmailOauth(body);
      setPlatformOauth(data);
      setPlatformOauthForm((f) => ({ ...f, google_client_secret: "", microsoft_client_secret: "" }));
      await load();
      toast.success("Credenciales OAuth de plataforma guardadas. Ya puede usar «Conectar con Google».");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "No se pudo guardar OAuth de plataforma");
    } finally {
      setSavingPlatformOauth(false);
    }
  };

  const handleConnectOAuth = async (provider) => {
    if (readOnly) return;
    setConnectingOAuth(provider);
    try {
      const data = await despachoMensajesGeneralesApi.startOAuth(provider);
      const authUrl = data?.auth_url;
      if (!authUrl) throw new Error("No se recibió URL de autorización");
      const width = 520;
      const height = 680;
      const left = window.screenX + Math.max(0, (window.outerWidth - width) / 2);
      const top = window.screenY + Math.max(0, (window.outerHeight - height) / 2);
      const popup = window.open(
        authUrl,
        "vf_email_oauth",
        `width=${width},height=${height},left=${left},top=${top},noopener,noreferrer`,
      );
      if (!popup) {
        toast.error("Permita ventanas emergentes para conectar su cuenta.");
        window.location.assign(authUrl);
      }
    } catch (err) {
      toast.error(parseMensajesGeneralesError(err).message);
    } finally {
      setConnectingOAuth("");
    }
  };

  const handleDisconnectOAuth = async () => {
    if (readOnly) return;
    if (!window.confirm("¿Desconectar la cuenta de correo vinculada?")) return;
    try {
      const cfg = await despachoMensajesGeneralesApi.disconnectOAuth();
      setCfgMeta(cfg);
      setForm((f) => ({
        ...f,
        email_transport: EMAIL_TRANSPORT_PLATFORM,
        use_platform_smtp: true,
      }));
      toast.success("Cuenta desconectada. Volvió al servidor de la plataforma.");
    } catch (err) {
      toast.error(parseMensajesGeneralesError(err).message);
    }
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || readOnly) return;
    setUploading(true);
    try {
      const data = await despachoMensajesGeneralesApi.uploadBrandImage(file);
      setCfgMeta(data.config || data);
      setPreviewToken((n) => n + 1);
      toast.success("Logo / firma cargado.");
    } catch (err) {
      toast.error(parseMensajesGeneralesError(err).message);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteImage = async () => {
    if (readOnly) return;
    if (!window.confirm("¿Quitar logo/firma del correo?")) return;
    try {
      const data = await despachoMensajesGeneralesApi.deleteBrandImage();
      setCfgMeta(data.config || data);
      setPreviewToken((n) => n + 1);
      toast.success("Imagen eliminada.");
    } catch (err) {
      toast.error(parseMensajesGeneralesError(err).message);
    }
  };

  if (loadState === "loading") {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-600 py-6">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        Cargando parámetros…
      </div>
    );
  }

  if (loadState === "error") {
    return (
      <div className="rounded-sm border border-red-200 bg-red-50 p-4 text-sm text-red-900">
        <p className="m-0">No se pudieron cargar los parámetros de correo.</p>
        <Button type="button" size="sm" variant="outline" className="mt-3" onClick={load}>
          Reintentar
        </Button>
      </div>
    );
  }

  const brandUrl = `${despachoMensajesGeneralesApi.brandImageUrl()}?t=${previewToken}`;
  const passwordHint =
    cfgMeta?.password_hint || smtpPresetForProvider(form.smtp_provider).passwordHint;
  const transportMode = form.email_transport || resolveEmailTransport({ ...cfgMeta, ...form });
  const platformEmailMode = transportMode === EMAIL_TRANSPORT_PLATFORM;
  const smtpEmailMode = transportMode === EMAIL_TRANSPORT_SMTP;
  const oauthProviders = cfgMeta?.oauth_providers || {};
  const connectedGoogle = cfgMeta?.oauth_connected && cfgMeta?.oauth_provider === "google";
  const connectedMicrosoft = cfgMeta?.oauth_connected && cfgMeta?.oauth_provider === "microsoft";
  const effectiveConfig = { ...cfgMeta, ...form, email_transport: transportMode };
  const pendingHint = smtpPendingHint(effectiveConfig);

  return (
    <div className="space-y-3" data-testid="calendario-email-params">
      <DespachoSectionCard title="Correo del despacho">
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label>Correo</Label>
            <Input
              type="email"
              className="mt-1 font-mono"
              disabled={readOnly}
              value={form.from_email}
              onChange={(e) => setForm((f) => syncSmtpProviderFromEmail(f, e.target.value))}
              placeholder="avisos@sudespacho.com"
              data-testid="email-params-from"
            />
          </div>
          <div>
            <Label>Nombre visible</Label>
            <Input
              className="mt-1"
              disabled={readOnly}
              value={form.from_name}
              onChange={(e) => setForm((f) => ({ ...f, from_name: e.target.value }))}
              placeholder="Despacho Contable"
            />
          </div>
        </div>

        {platformEmailMode ? (
          <p className="m-0 mt-3 text-[11px] text-slate-500">
            Los correos salen por la plataforma BNT. Las respuestas llegan a su correo.
          </p>
        ) : null}

        <div className="mt-3 flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={form.enabled}
              disabled={readOnly}
              onCheckedChange={(v) => setForm((f) => ({ ...f, enabled: Boolean(v) }))}
              data-testid="email-params-enabled"
            />
            Activar envíos
          </label>
          {cfgMeta?.transport_ready ? (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-800">
              <MailCheck className="h-3.5 w-3.5" />
              {emailTransportStatusLabel(cfgMeta)}
            </span>
          ) : pendingHint ? (
            <span className="text-xs text-amber-800">{pendingHint}</span>
          ) : (
            <span className="text-xs text-amber-800">{emailTransportStatusLabel(effectiveConfig)}</span>
          )}
        </div>

        {platformEmailMode && !platformSmtpReady(cfgMeta) && !cfgMeta?.transport_ready ? (
          <div
            className="mt-3 rounded-sm border border-amber-300 bg-amber-50 p-3 space-y-2"
            data-testid="email-params-platform-fallback"
          >
            <p className="m-0 text-sm font-medium text-amber-950">
              El envío por plataforma BNT no está activo en este servidor.
            </p>
            <p className="m-0 text-xs text-amber-900">
              «Activar envíos» no basta. Conecte su Gmail (ya tiene OAuth configurado) para poder probar el envío.
            </p>
            {!readOnly ? (
              <div className="flex flex-wrap gap-2">
                {oauthProviders.google?.configured ? (
                  <Button
                    type="button"
                    size="sm"
                    className="bg-[#0F2D5C]"
                    disabled={connectingOAuth === "google"}
                    onClick={() => {
                      setAdvancedTransportOpen(true);
                      handleTransportChange(EMAIL_TRANSPORT_OAUTH_GOOGLE);
                      handleConnectOAuth("google");
                    }}
                    data-testid="email-params-cta-conectar-google"
                  >
                    {connectingOAuth === "google" ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      "Conectar Google ahora"
                    )}
                  </Button>
                ) : null}
                {oauthProviders.microsoft?.configured ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={connectingOAuth === "microsoft"}
                    onClick={() => {
                      setAdvancedTransportOpen(true);
                      handleTransportChange(EMAIL_TRANSPORT_OAUTH_MICROSOFT);
                      handleConnectOAuth("microsoft");
                    }}
                    data-testid="email-params-cta-conectar-microsoft"
                  >
                    {connectingOAuth === "microsoft" ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      "Conectar Microsoft"
                    )}
                  </Button>
                ) : null}
                {!oauthProviders.google?.configured && !oauthProviders.microsoft?.configured ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setAdvancedTransportOpen(true);
                      if (canEditPlatformOauth) openPlatformOauthPanel();
                    }}
                  >
                    Abrir configuración de correo
                  </Button>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}

        {!readOnly ? (
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              type="button"
              className="bg-[#0F2D5C]"
              disabled={saving}
              onClick={handleSave}
              data-testid="email-params-save"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar"}
            </Button>
            {cfgMeta?.transport_ready && onOpenEnvioLibre ? (
              <Button
                type="button"
                variant="outline"
                onClick={onOpenEnvioLibre}
                data-testid="email-params-go-envio-libre"
              >
                Probar envío
              </Button>
            ) : null}
          </div>
        ) : null}

        {platformEmailMode ? (
          <div className="mt-3">
            <PlatformEmailSetupGuide />
          </div>
        ) : null}
      </DespachoSectionCard>

      <details
        className="group rounded-sm border border-slate-200 bg-white"
        open={advancedTransportOpen}
        onToggle={(event) => setAdvancedTransportOpen(event.currentTarget.open)}
        data-testid="email-params-advanced-transport"
      >
        <summary className="flex cursor-pointer list-none items-center justify-between gap-2 p-3 text-sm font-semibold text-[#0F2D5C]">
          Usar mi Gmail, Outlook u otro correo
          <ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180" />
        </summary>
        <div className="space-y-3 border-t border-slate-200 p-3">
          <div>
            <Label>Forma de envío</Label>
            <Select
              value={transportMode}
              disabled={readOnly}
              onValueChange={handleTransportChange}
            >
              <SelectTrigger className="mt-1 rounded-sm" data-testid="email-params-transport">
                <SelectValue placeholder="Seleccione" />
              </SelectTrigger>
              <SelectContent>
                {EMAIL_TRANSPORT_OPTIONS.map((opt) => (
                  <SelectItem key={opt.id} value={opt.id}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {platformEmailMode && !platformSmtpReady(cfgMeta) ? (
            <p className="m-0 text-[11px] text-amber-800">
              El SMTP de la plataforma BNT no está activo en este servidor.
              Elija «Conectar Google» o «Conectar Microsoft» abajo para enviar con su cuenta.
            </p>
          ) : null}

          {transportMode === EMAIL_TRANSPORT_OAUTH_GOOGLE ? (
            <div className="rounded-sm border border-slate-200 bg-slate-50 p-3 text-xs space-y-2">
              {connectedGoogle ? (
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="m-0 flex items-center gap-2 text-emerald-800">
                    <CheckCircle2 className="h-4 w-4" />
                    <span className="font-mono">{cfgMeta.oauth_email}</span>
                  </p>
                  {!readOnly ? (
                    <Button type="button" size="sm" variant="outline" onClick={handleDisconnectOAuth}>
                      Desconectar
                    </Button>
                  ) : null}
                </div>
              ) : (
                !readOnly ? <div className="space-y-2">{renderOAuthConnectActions("google")}</div> : null
              )}
              <GoogleBuyerSetupGuide oauthReady={Boolean(oauthProviders.google?.configured)} />
            </div>
          ) : null}

          {transportMode === EMAIL_TRANSPORT_OAUTH_MICROSOFT ? (
            <div className="rounded-sm border border-slate-200 bg-slate-50 p-3 text-xs space-y-2">
              {connectedMicrosoft ? (
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="m-0 flex items-center gap-2 text-emerald-800">
                    <CheckCircle2 className="h-4 w-4" />
                    <span className="font-mono">{cfgMeta.oauth_email}</span>
                  </p>
                  {!readOnly ? (
                    <Button type="button" size="sm" variant="outline" onClick={handleDisconnectOAuth}>
                      Desconectar
                    </Button>
                  ) : null}
                </div>
              ) : (
                !readOnly ? <div className="space-y-2">{renderOAuthConnectActions("microsoft")}</div> : null
              )}
              <MicrosoftBuyerSetupGuide oauthReady={Boolean(oauthProviders.microsoft?.configured)} />
            </div>
          ) : null}

          {smtpEmailMode ? (
            <div className="rounded-sm border border-slate-200 bg-slate-50 p-3">
              {isMicrosoftEmail(form.from_email) ? (
                <div className="space-y-2 text-xs text-amber-950">
                  <p className="m-0">Outlook/Hotmail/Live: use Conectar Microsoft.</p>
                  {!readOnly ? (
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => handleTransportChange(EMAIL_TRANSPORT_OAUTH_MICROSOFT)}
                    >
                      Conectar Microsoft
                    </Button>
                  ) : null}
                </div>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <Label>Proveedor</Label>
                    <Select
                      value={form.smtp_provider || SMTP_PROVIDER_CUSTOM}
                      onValueChange={(value) => setForm((f) => applySmtpProviderToForm(f, value, { fromEmail: f.from_email }))}
                      disabled={readOnly}
                    >
                      <SelectTrigger className="mt-1" data-testid="email-params-provider">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SMTP_PROVIDERS.filter((row) => row.id !== "microsoft").map((row) => (
                          <SelectItem key={row.id} value={row.id}>{row.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Usuario SMTP</Label>
                    <Input
                      className="mt-1 font-mono"
                      disabled={readOnly}
                      value={form.smtp_user}
                      onChange={(e) => setForm((f) => ({ ...f, smtp_user: e.target.value }))}
                      placeholder={form.from_email || "correo"}
                    />
                  </div>
                  <div>
                    <Label>Contraseña de aplicación</Label>
                    <PasswordInput
                      className="mt-1"
                      disabled={readOnly}
                      value={form.smtp_password}
                      onChange={(e) => setForm((f) => ({
                        ...f,
                        smtp_password: e.target.value,
                        clear_smtp_password: false,
                      }))}
                      placeholder={cfgMeta?.has_smtp_password ? "(guardada)" : "App password"}
                      autoComplete="new-password"
                      data-testid="email-params-smtp-password"
                    />
                    <p className="text-[10px] text-slate-600 m-0 mt-1">{passwordHint}</p>
                    {form.smtp_provider === "gmail" ? (
                      <a
                        href="https://myaccount.google.com/apppasswords"
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-[11px] underline mt-1"
                      >
                        Generar en Google
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    ) : null}
                  </div>
                  {form.smtp_provider === SMTP_PROVIDER_CUSTOM ? (
                    <>
                      <div>
                        <Label>Servidor</Label>
                        <Input
                          className="mt-1 font-mono"
                          disabled={readOnly}
                          value={form.smtp_host}
                          onChange={(e) => setForm((f) => ({ ...f, smtp_host: e.target.value }))}
                          placeholder="smtp.proveedor.com"
                        />
                      </div>
                      <div>
                        <Label>Puerto</Label>
                        <Input
                          type="number"
                          className="mt-1"
                          disabled={readOnly}
                          value={form.smtp_port}
                          onChange={(e) => setForm((f) => ({ ...f, smtp_port: e.target.value }))}
                        />
                      </div>
                      <label className="flex items-center gap-2 text-sm sm:col-span-2">
                        <Checkbox
                          checked={form.smtp_use_tls}
                          disabled={readOnly}
                          onCheckedChange={(v) => setForm((f) => ({ ...f, smtp_use_tls: Boolean(v) }))}
                        />
                        STARTTLS
                      </label>
                    </>
                  ) : null}
                  {!readOnly && cfgMeta?.has_smtp_password ? (
                    <label className="flex items-center gap-2 text-xs text-slate-600 sm:col-span-2">
                      <Checkbox
                        checked={form.clear_smtp_password}
                        onCheckedChange={(v) => setForm((f) => ({
                          ...f,
                          clear_smtp_password: Boolean(v),
                        }))}
                      />
                      Borrar contraseña guardada
                    </label>
                  ) : null}
                  {!readOnly ? (
                    <div className="sm:col-span-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={testingSmtp || isMicrosoftEmail(form.from_email)}
                        onClick={handleTestSmtp}
                        data-testid="email-params-test-smtp"
                      >
                        {testingSmtp ? <Loader2 className="h-4 w-4 animate-spin" /> : "Probar SMTP"}
                      </Button>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          ) : null}

          {smtpEmailMode ? <SmtpBuyerSetupGuide /> : null}

          {!readOnly && !platformEmailMode ? (
            <Button
              type="button"
              size="sm"
              className="bg-[#0F2D5C]"
              disabled={saving}
              onClick={handleSave}
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar"}
            </Button>
          ) : null}
        </div>
      </details>

      <details className="group rounded-sm border border-slate-200 bg-white">
        <summary className="flex cursor-pointer list-none items-center justify-between gap-2 p-3 text-sm font-semibold text-[#0F2D5C]">
          Pie, asunto y logo (opcional)
          <ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180" />
        </summary>
        <div className="space-y-4 border-t border-slate-200 p-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label>Reply-To (opcional)</Label>
              <Input
                type="email"
                className="mt-1 font-mono"
                disabled={readOnly}
                value={form.reply_to}
                onChange={(e) => setForm((f) => ({ ...f, reply_to: e.target.value }))}
              />
            </div>
            <div>
              <Label>Prefijo de asunto</Label>
              <Input
                className="mt-1"
                disabled={readOnly}
                value={form.subject_prefix}
                onChange={(e) => setForm((f) => ({ ...f, subject_prefix: e.target.value }))}
                placeholder="[Despacho]"
              />
            </div>
          </div>
          <div>
            <Label>Asunto por defecto</Label>
            <Input
              className="mt-1"
              disabled={readOnly}
              value={form.default_subject}
              onChange={(e) => setForm((f) => ({ ...f, default_subject: e.target.value }))}
              data-testid="email-params-subject"
            />
          </div>
          <div>
            <Label>Texto base</Label>
            <SpellcheckTextarea
              className="mt-1 min-h-[80px]"
              value={form.default_body}
              disabled={readOnly}
              onChange={(e) => setForm((f) => ({ ...f, default_body: e.target.value }))}
            />
          </div>
          <div>
            <label className="flex items-center gap-2 text-sm mb-2">
              <Checkbox
                checked={form.append_footer}
                disabled={readOnly}
                onCheckedChange={(v) => setForm((f) => ({ ...f, append_footer: Boolean(v) }))}
              />
              Pie al final de cada envío
            </label>
            <SpellcheckTextarea
              className="min-h-[80px]"
              value={form.footer_text}
              disabled={readOnly}
              onChange={(e) => setForm((f) => ({ ...f, footer_text: e.target.value }))}
              placeholder={"Atentamente,\nDespacho Contable"}
            />
          </div>
          <div>
            <Label>Logo o firma</Label>
            {cfgMeta?.has_brand_image ? (
              <div className="mt-2 space-y-2">
                <img
                  src={brandUrl}
                  alt="Logo o firma"
                  className="max-h-24 rounded-sm border border-slate-200 bg-white p-1"
                  crossOrigin="use-credentials"
                />
              </div>
            ) : null}
            {!readOnly ? (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <label className="inline-flex cursor-pointer items-center gap-1 rounded-sm border px-2 py-1.5 text-xs hover:bg-slate-50">
                  {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                  Subir
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/gif,image/webp"
                    className="hidden"
                    onChange={handleUpload}
                    disabled={uploading}
                  />
                </label>
                {cfgMeta?.has_brand_image ? (
                  <Button type="button" size="sm" variant="ghost" className="text-red-700" onClick={handleDeleteImage}>
                    <Trash2 className="mr-1 h-3.5 w-3.5" />
                    Quitar
                  </Button>
                ) : null}
              </div>
            ) : null}
          </div>
          {!readOnly ? (
            <Button type="button" size="sm" variant="outline" disabled={saving} onClick={handleSave}>
              Guardar personalización
            </Button>
          ) : null}
        </div>
      </details>

      {canEditPlatformOauth ? (
        <details
          ref={platformOauthRef}
          open={platformOauthOpen}
          onToggle={(event) => setPlatformOauthOpen(event.currentTarget.open)}
          className="group rounded-sm border border-amber-300 bg-amber-50"
          data-testid="email-platform-oauth-panel"
        >
          <summary className="flex cursor-pointer list-none items-center justify-between gap-2 p-3 text-sm font-semibold text-amber-950">
            <span className="inline-flex items-center gap-2">
              <ShieldCheck className="h-4 w-4" />
              Conexión de la plataforma (operador)
            </span>
            <ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180" />
          </summary>
          <div className="grid gap-3 border-t border-amber-200 p-3 sm:grid-cols-2">
            {platformOauth?.encryption_ready === false ? (
              <p className="sm:col-span-2 m-0 text-xs text-red-900">
                Falta VF_EMAIL_CREDENTIALS_ENCRYPTION_KEY en Railway.
              </p>
            ) : null}
            <div className="space-y-2">
              <p className="m-0 text-xs font-semibold">Google</p>
              <p className="m-0 text-[10px] text-amber-900">URL de regreso (cópiela tal cual en Google)</p>
              <Input
                readOnly
                className="h-8 font-mono text-[10px]"
                value={platformOauth?.google_redirect_uri || cfgMeta?.oauth_google_redirect_uri || ""}
              />
              <p className="m-0 text-[10px] text-amber-900">Identificador de la aplicación</p>
              <Input
                className="h-8 font-mono text-xs"
                placeholder="Ej. 123456789-abc.apps.googleusercontent.com"
                value={platformOauthForm.google_client_id}
                onChange={(e) => setPlatformOauthForm((f) => ({ ...f, google_client_id: e.target.value }))}
              />
              <p className="m-0 text-[10px] text-amber-900">Llave secreta</p>
              <PasswordInput
                className="h-8 font-mono text-xs"
                placeholder={platformOauth?.has_google_secret ? "Secret (guardado)" : "Pegue el secreto de Google"}
                value={platformOauthForm.google_client_secret}
                onChange={(e) => setPlatformOauthForm((f) => ({ ...f, google_client_secret: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <p className="m-0 text-xs font-semibold">Microsoft</p>
              <p className="m-0 text-[10px] text-amber-900">URL de regreso (cópiela tal cual en Microsoft)</p>
              <Input
                readOnly
                className="h-8 font-mono text-[10px]"
                value={platformOauth?.microsoft_redirect_uri || ""}
              />
              <p className="m-0 text-[10px] text-amber-900">Identificador de la aplicación</p>
              <Input
                className="h-8 font-mono text-xs"
                placeholder="Id. de aplicación (cliente)"
                value={platformOauthForm.microsoft_client_id}
                onChange={(e) => setPlatformOauthForm((f) => ({ ...f, microsoft_client_id: e.target.value }))}
              />
              <p className="m-0 text-[10px] text-amber-900">Llave secreta</p>
              <PasswordInput
                className="h-8 font-mono text-xs"
                placeholder={platformOauth?.has_microsoft_secret ? "Secret (guardado)" : "Valor del secreto (solo se ve una vez)"}
                value={platformOauthForm.microsoft_client_secret}
                onChange={(e) => setPlatformOauthForm((f) => ({ ...f, microsoft_client_secret: e.target.value }))}
              />
              <p className="m-0 text-[10px] text-amber-900">Tenant (deje «common» si no le indican otro)</p>
              <Input
                className="h-8 font-mono text-xs"
                placeholder="common"
                value={platformOauthForm.microsoft_tenant}
                onChange={(e) => setPlatformOauthForm((f) => ({ ...f, microsoft_tenant: e.target.value }))}
              />
            </div>
            <div className="sm:col-span-2 flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 text-sm font-medium text-amber-950">
                <Checkbox
                  checked={form.enabled}
                  disabled={readOnly}
                  onCheckedChange={(v) => setForm((f) => ({ ...f, enabled: Boolean(v) }))}
                  data-testid="email-params-enabled-oauth-panel"
                />
                Activar envíos
              </label>
              <Button
                type="button"
                size="sm"
                disabled={savingPlatformOauth || platformOauth?.encryption_ready === false}
                onClick={handleSavePlatformOauth}
                data-testid="email-params-save-platform-oauth"
              >
                {savingPlatformOauth ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar conexión"}
              </Button>
              {!readOnly ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="border-amber-400 bg-white text-amber-950 hover:bg-amber-100"
                  disabled={saving}
                  onClick={handleSave}
                  data-testid="email-params-save-enabled-oauth-panel"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar envíos"}
                </Button>
              ) : null}
              <p className="m-0 w-full text-[10px] text-amber-900">
                Marque «Activar envíos» y pulse «Guardar envíos» para permitir salida de correo.
                «Guardar conexión» solo guarda el identificador y el secreto de Google y Microsoft.
              </p>
            </div>
            <OperatorOauthSetupGuide
              googleRedirect={platformOauth?.google_redirect_uri || cfgMeta?.oauth_google_redirect_uri || ""}
              microsoftRedirect={platformOauth?.microsoft_redirect_uri || ""}
            />
          </div>
        </details>
      ) : null}
    </div>
  );
}
